/* @img-migrated */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Search, Building2, Star, MapPin, ArrowRight, Filter } from 'lucide-react';
import { Input } from '../../components/ui/input';

import Img from '../../components/Img';
import { resolveImg } from '../../lib/images';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PLAN_STYLES = {
  Executive: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
  Premier:   { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
  Essential: { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200' },
  Hybrid:    { bg: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' },
};

const INDUSTRIES = [
  { value: '', label: 'All Industries' },
  { value: 'restaurants', label: 'Restaurants & Hospitality' },
  { value: 'professional', label: 'Professional Services' },
  { value: 'health', label: 'Health & Wellness' },
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'finance', label: 'Finance' },
  { value: 'marketing', label: 'Marketing & Media' },
  { value: 'technology', label: 'Technology' },
  { value: 'education', label: 'Education' },
  { value: 'retail', label: 'Retail' },
];

const PortalBusinessDirectory = () => {
  const [businesses, setBusinesses] = useState([]);
  const [search, setSearch] = useState('');
  const [industry, setIndustry] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => { fetchBusinesses(); }, [search, industry, planFilter]);  

  const fetchBusinesses = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set('search', search);
      if (industry) params.set('industry', industry);
      if (planFilter) params.set('plan', planFilter);
      const res = await axios.get(`${API}/portal/businesses?${params}`, { withCredentials: true });
      setBusinesses(res.data);
    } catch { /* */ } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#F0F2F5] py-5 px-4" data-testid="portal-business-directory">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-[22px] sm:text-[26px] font-bold text-[#1A1A1A] tracking-[-0.02em]">Business Directory</h1>
          <p className="text-[13px] text-[#8C8C8C] mt-1">{businesses.length} businesses in the DCCF community</p>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6">
          <div className="relative flex-1 max-w-sm w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8C8C]" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search businesses..." className="pl-10 h-10 rounded-xl bg-white border-slate-200/80 text-[13px]" data-testid="business-search" />
          </div>
          <div className="flex gap-2">
            <select value={industry} onChange={e => setIndustry(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-slate-200/80 text-[13px] text-[#555] focus:outline-none focus:ring-2 focus:ring-[#002249]/10" data-testid="industry-filter">
              {INDUSTRIES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
            </select>
            <select value={planFilter} onChange={e => setPlanFilter(e.target.value)} className="h-10 px-3 rounded-xl bg-white border border-slate-200/80 text-[13px] text-[#555] focus:outline-none focus:ring-2 focus:ring-[#002249]/10" data-testid="plan-filter">
              <option value="">All Plans</option>
              <option value="Executive">Executive</option>
              <option value="Premier">Premier</option>
              <option value="Essential">Essential</option>
              <option value="Hybrid">Hybrid</option>
            </select>
          </div>
        </div>

        {/* Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin w-8 h-8 border-4 border-[#002249] border-t-transparent rounded-full" /></div>
        ) : businesses.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-12 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-[15px] font-semibold text-[#1A1A1A]">No businesses found</p>
            <p className="text-[13px] text-[#8C8C8C] mt-1">{search ? 'Try a different search.' : 'Businesses will appear here.'}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {businesses.map((biz, i) => {
              const ps = PLAN_STYLES[biz.plan_name] || PLAN_STYLES.Hybrid;
              return (
                <motion.div key={biz.user_id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.03, 0.2) }}>
                  <Link to={`/portal/business/${biz.user_id}`} className="block bg-white rounded-xl border border-slate-200/80 shadow-[0_1px_3px_rgba(0,0,0,0.04)] hover:shadow-md hover:border-slate-300/80 transition-all group" data-testid={`business-card-${i}`}>
                    <div className="p-5">
                      {/* Logo + Plan badge row */}
                      <div className="flex items-start justify-between mb-4">
                        {biz.logo_url ? (
                          <div
                            className="w-16 h-16 rounded-xl bg-white ring-1 ring-slate-200/80 shadow-sm hover:shadow-md transition-shadow flex items-center justify-center overflow-hidden flex-shrink-0 p-2"
                            data-testid={`business-logo-tile-${i}`}
                          >
                            <Img variant="business"
                              src={resolveImg(biz.logo_url)}
                              alt={biz.company_name}
                              className="max-w-full max-h-full object-contain"
                              loading="lazy"
                              decoding="async"
                            />
                          </div>
                        ) : (
                          <div
                            className="w-16 h-16 rounded-xl bg-gradient-to-br from-[#002249] to-[#0a3570] flex items-center justify-center flex-shrink-0 shadow-sm"
                            data-testid={`business-fallback-tile-${i}`}
                          >
                            <span className="text-white font-bold text-lg tracking-tight">
                              {(biz.company_name || '?').trim().charAt(0).toUpperCase()}
                            </span>
                          </div>
                        )}
                        <div className="flex items-center gap-1.5">
                          {biz.is_founding_member && <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />}
                          {biz.plan_name && <span className={`text-[10px] font-bold px-2 py-[2px] rounded-full border ${ps.bg} ${ps.text} ${ps.border}`}>{biz.plan_name}</span>}
                        </div>
                      </div>
                      {/* Company Name */}
                      <h3 className="text-[15px] font-bold text-[#1A1A1A] leading-snug mb-1 group-hover:text-[#002249] transition-colors line-clamp-1">{biz.company_name}</h3>
                      {/* Contact name */}
                      <p className="text-[12px] text-[#8C8C8C] mb-2.5">{biz.contact_name}</p>
                      {/* Description */}
                      {biz.description && <p className="text-[13px] text-[#555] line-clamp-2 leading-relaxed mb-3">{biz.description}</p>}
                      {/* Footer */}
                      <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                        {biz.industry ? (
                          <span className="text-[11px] text-[#8C8C8C] capitalize">{biz.industry.replace('_', ' ')}</span>
                        ) : <span />}
                        <span className="text-[12px] text-[#E63946] font-semibold flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          View <ArrowRight className="w-3 h-3" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default PortalBusinessDirectory;
